/* Sales Ladder — pure helpers shared by the compact page: the mode flags, stage
   keys/labels for the adaptable policy, and fixed-ladder validation/summaries. */

import type { Account, FixedLadder, FixedLadderBody, SalesMediaItem, SalesPolicy, SalesPolicyBody } from '../../../../api/client';
import { MIN_PAID_PRICE, bodyToPolicy, policyToBody, stageKey, teasingStageKey, type SPolicy, type SStage } from '../../Content/salesB/model';
import type { DraftStep } from '../../SalesLadder/types';

/* ── Mode ───────────────────────────────────────────────────────── */

// The three exclusive content-selection modes. The pair of account flags encodes
// them (auto > fixed > adaptable).
export type LadderMode = 'fixed' | 'adaptable' | 'auto';
export const MODES: LadderMode[] = ['fixed', 'adaptable', 'auto'];

/** Auto wins when set; otherwise an unset fixed flag defaults to Fixed (the DB default). */
export function deriveMode(a: Account): LadderMode {
  if (a.is_auto_ladder_active) return 'auto';
  if (a.is_fixed_ladder_active ?? true) return 'fixed';
  return 'adaptable';
}

/** Exactly one mode on, so fixed and auto can never both be set. */
export function modeFlags(m: LadderMode): { is_fixed_ladder_active: boolean; is_auto_ladder_active: boolean } {
  return { is_fixed_ladder_active: m === 'fixed', is_auto_ladder_active: m === 'auto' };
}

/* ── Adaptable policy (stages + negotiation + alerts + prompts) ──── */

/** The editor draft plus the last server body, so unmapped fields survive a save. */
export interface PolicyDraft { s: SPolicy; prev: SalesPolicyBody }

export function draftOf(p: SalesPolicy): PolicyDraft {
  // Keep only the writable body; the read-only echo fields don't go back up.
  const body: SalesPolicyBody = {
    is_active: p.is_active, restrict_to_one_vault: p.restrict_to_one_vault, sales_stages_settings: p.sales_stages_settings,
    absolute_min_price: p.absolute_min_price, max_negotiation_attempts: p.max_negotiation_attempts, on_max_negotiations: p.on_max_negotiations,
    negotiation_grace_turns: p.negotiation_grace_turns, max_negotiation_turns: p.max_negotiation_turns, negotiation_prompt: p.negotiation_prompt,
    sales_strategy_prompt: p.sales_strategy_prompt, notify_on_completion: p.notify_on_completion, notify_on_decline: p.notify_on_decline,
    continue_sales_on_decline: p.continue_sales_on_decline, notify_when_fan_busy: p.notify_when_fan_busy, notify_when_fan_disengaged: p.notify_when_fan_disengaged,
  };
  return { s: bodyToPolicy(body), prev: body };
}

export function bodyOfDraft(d: PolicyDraft): SalesPolicyBody {
  // A hard decline always ends the ladder (product decision) — the toggle is gone.
  return { ...policyToBody(d.s, d.prev), continue_sales_on_decline: false };
}

/** The key each stage is stored under (and that items reference as `sales_stage`),
 *  in the same order policyToBody writes them. */
export function stageKeys(stages: SStage[]): string[] {
  const teasers = stages.filter((s) => s.kind === 'free').length;
  let ti = 0;
  let si = 0;
  return stages.map((s) => (s.kind === 'free' ? teasingStageKey(++ti, teasers) : stageKey(++si)));
}

/** Short display names: "Teasing" (or "Teasing 1…") and "Sale 1…". */
export function stageNames(stages: SStage[], t: (k: string, o?: Record<string, unknown>) => string): string[] {
  const teasers = stages.filter((s) => s.kind === 'free').length;
  let ti = 0;
  let si = 0;
  return stages.map((s) => (s.kind === 'free' ? (teasers > 1 ? t('sl.teasingN', { n: ++ti }) : t('sl.teasing')) : t('sl.saleN', { n: ++si })));
}

export const money = (n: number | null | undefined) => (n ? '$' + Number(n).toFixed(2).replace(/\.00$/, '') : '');

/** Hold the save back while something would round-trip wrong or be rejected. */
export function validateDraft(d: PolicyDraft, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  const names = stageNames(d.s.stages, t);
  const low = (n: number | null | undefined) => n != null && n > 0 && n < MIN_PAID_PRICE;
  for (let i = 0; i < d.s.stages.length; i++) {
    const st = d.s.stages[i]!;
    // A $0 sale stage would be read back as a teasing stage, so it can't be saved.
    if (st.kind === 'sale' && !st.price) return t('sl.err.noPrice', { name: names[i] });
    if (st.kind === 'sale' && (low(st.price) || low(st.floor))) return t('sl.err.minPaid', { name: names[i] });
    if (st.kind === 'sale' && st.floor > st.price) return t('sl.err.floorAbove', { name: names[i] });
    if (st.photosMin != null && st.photosMax != null && st.photosMax < st.photosMin) return t('sl.err.range', { name: names[i] });
    if (st.videosMin != null && st.videosMax != null && st.videosMax < st.videosMin) return t('sl.err.range', { name: names[i] });
  }
  if (low(d.s.negotiation.absMinPrice)) return t('sl.err.absMin');
  return null;
}

let nid = 0;
export const newStageId = () => `n${Date.now().toString(36)}${++nid}`;

export function newStage(kind: 'free' | 'sale'): SStage {
  return { id: newStageId(), name: '', kind, price: 0, floor: 0, guidance: '', offerFraming: '', photosMin: null, photosMax: null, videosMin: null, videosMax: null };
}

/* ── Adaptable content items ────────────────────────────────────── */

export interface PoolItem extends SalesMediaItem { vault_id: string; vault_name: string }

export const isAudio = (it: SalesMediaItem) => (it.type ?? '').toLowerCase() === 'audio';
/** "Analysed" in the sales flow = the AI wrote a description (which also sets the stage). */
export const isDescribed = (it: SalesMediaItem) => !!(it.description ?? '').trim();
export const canDescribe = (it: SalesMediaItem) => !isDescribed(it) && !isAudio(it);

/* ── Fixed ladders ──────────────────────────────────────────────── */

/** Rungs are named from their type + position among rungs of that type. */
export function rungLabel(steps: { type: 'teasing' | 'sale' }[], i: number): string {
  const type = steps[i]!.type;
  const n = steps.slice(0, i + 1).filter((s) => s.type === type).length;
  return type === 'sale' ? `Sale ${n}` : `Teasing ${n}`;
}

/** Blocking problems (parity with the backend's validate_steps). */
export function ladderIssues(steps: { type: 'teasing' | 'sale'; content_ids: string[]; price: number; min_price: number }[], t: (k: string, o?: Record<string, unknown>) => string): string[] {
  const out: string[] = [];
  if (!steps.length) out.push(t('sl.fx.errNoRungs'));
  steps.forEach((s, i) => {
    const ref = rungLabel(steps, i);
    if (!s.content_ids.length) out.push(t('sl.fx.errNoMedia', { ref }));
    if (s.type === 'sale') {
      if (!s.price) out.push(t('sl.fx.errNoPrice', { ref }));
      else if (s.price < MIN_PAID_PRICE || (s.min_price > 0 && s.min_price < MIN_PAID_PRICE)) out.push(t('sl.fx.errMinPaid', { ref }));
      else if (s.min_price < 0 || s.min_price > s.price) out.push(t('sl.fx.errFloor', { ref }));
    }
  });
  return out;
}

export const ladderName = (l: Pick<FixedLadderBody, 'usage_notes'>) => ((l.usage_notes || '').split('\n')[0] ?? '').trim();

/** Replace the first line of usage_notes (the ladder's name), keeping any notes below it. */
export function withName(notes: string | null, name: string): string | null {
  const rest = (notes || '').split('\n').slice(1);
  const out = [name, ...rest].join('\n');
  return out.trim() ? out : null;
}

export function ladderBody(l: FixedLadder, over: Partial<FixedLadderBody> = {}): FixedLadderBody {
  return { priority: l.priority, usage_notes: l.usage_notes, is_active: l.is_active, steps: l.steps, ...over };
}

/** The persisted steps for a draft: teasing rungs are free and carry no previews;
 *  offer framing is sent only when typed (so an unedited rung keeps the default). */
export function stepsOf(steps: DraftStep[]): FixedLadderBody['steps'] {
  return steps.map((s, i) => ({
    label: rungLabel(steps, i),
    type: s.type,
    content_ids: s.content_ids,
    content_vaults: s.content_vaults,
    price: s.type === 'teasing' ? 0 : s.price,
    min_price: s.type === 'teasing' ? 0 : s.min_price,
    preview_ids: s.type === 'teasing' ? [] : s.preview_ids,
    ...(s.type === 'sale' && s.offer_framing && s.offer_framing.trim() ? { offer_framing: s.offer_framing.trim() } : {}),
  }));
}
